import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import {
  usePrepCandidates, usePrepPlan, useSavePrepPlan, usePrepPreview,
  usePlannedPrepCandidates, usePlannedPrepPlan, useSavePlannedPrepPlan, usePlannedPrepPreview,
} from "@/lib/sessionPrepQueries";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const DOCUMENT_GROUPS = [
  ["work_document", "work_documents", "WorkDocuments"], ["file_asset", "file_assets", "Files & Videos"],
  ["feedback", "feedback", "Feedback"], ["presentation", "presentations", "Presentations"],
];

const AUDIENCE_LABELS = { team: "Team", coach: "Coach" };

/**
 * Participant-Specific Session Resource Planning — Prepare Session is now
 * curated PER AUDIENCE TAB ("Team" | each client participant | "Coach")
 * instead of one flat resource list. `resources[]` stays the canonical,
 * deduped catalog of resources added to this plan (a resource may be
 * added once but referenced by several tabs); `audienceAssignments[]` is
 * the separate (resource_id, audience_key) mapping that decides which
 * tab(s) each one is curated into — exactly mirroring the backend's
 * SessionPrepPlan.audience_assignments. Session Tools stay a SEPARATE,
 * always-shared section untouched by audience curation (see
 * build_session_room_view_for_participant's own "tools are never
 * audience-assigned" rule) — same mechanism as before this feature.
 */
export default function SessionPrepView({ workspaceId, sesId, planned = false, memberId }) {
  const [activeTab, setActiveTab] = useState(null);
  const sessionCandidatesBase = usePrepCandidates(workspaceId, !planned ? sesId : null);
  const plannedCandidatesBase = usePlannedPrepCandidates(workspaceId, planned ? memberId : null);
  const sessionCandidatesTab = usePrepCandidates(workspaceId, !planned ? sesId : null, activeTab || null);
  const plannedCandidatesTab = usePlannedPrepCandidates(workspaceId, planned ? memberId : null, activeTab || null);
  const sessionPlan = usePrepPlan(workspaceId, !planned ? sesId : null);
  const plannedPlan = usePlannedPrepPlan(workspaceId, planned ? memberId : null);
  const saveSessionPlan = useSavePrepPlan(workspaceId, sesId);
  const saveLocalPlanned = useSavePlannedPrepPlan(workspaceId, memberId);
  const savePlan = planned ? saveLocalPlanned : saveSessionPlan;

  const { data: baseCandidates, isPending: candidatesPending, isError: candidatesError } = planned ? plannedCandidatesBase : sessionCandidatesBase;
  const { data: tabCandidates } = planned ? plannedCandidatesTab : sessionCandidatesTab;
  const { data: plan, isPending: planPending, isError: planError } = planned ? plannedPlan : sessionPlan;

  const [resources, setResources] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [audienceAssignments, setAudienceAssignments] = useState([]);
  const [previewFor, setPreviewFor] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState("");

  useEffect(() => {
    if (plan) {
      setResources((plan.resources || []).map((r) => ({ resource_id: r.resource_id, resource_type: r.resource_type, label: r.label, order_index: r.order_index, is_default: r.is_default })));
      setOverrides(plan.block_overrides || []);
      setAudienceAssignments((plan.audience_assignments || []).map((a) => ({ resource_id: a.resource_id, audience_key: a.audience_key, order_index: a.order_index, is_default: a.is_default })));
    }
  }, [plan]);
  useEffect(() => {
    if (baseCandidates?.participants?.length && !previewFor) setPreviewFor(baseCandidates.participants[0].membership_id);
  }, [baseCandidates, previewFor]);

  const audienceTabs = useMemo(() => {
    if (!baseCandidates) return [];
    const clientParticipants = baseCandidates.participants.filter((p) => p.role !== "coach");
    return [
      ...(clientParticipants.length >= 2 ? [{ key: "team", label: "Team" }] : []),
      ...clientParticipants.map((p) => ({ key: p.membership_id, label: p.name })),
      { key: "coach", label: "Coach" },
    ];
  }, [baseCandidates]);

  useEffect(() => {
    if (!audienceTabs.length) return;
    if (!activeTab || !audienceTabs.some((t) => t.key === activeTab)) setActiveTab(audienceTabs[0].key);
  }, [audienceTabs, activeTab]);

  const sessionPreview = usePrepPreview(workspaceId, !planned ? sesId : null, !planned ? previewFor : null);
  const plannedPreview = usePlannedPrepPreview(workspaceId, planned ? memberId : null);
  const { data: preview } = planned ? plannedPreview : sessionPreview;

  function addResourceToActiveTab(resourceType, candidate) {
    if (!activeTab) return;
    const alreadyInPlan = resources.some((r) => r.resource_id === candidate.resource_id);
    setResources((prev) => (prev.some((r) => r.resource_id === candidate.resource_id) ? prev : [...prev, { resource_id: candidate.resource_id, resource_type: resourceType, label: candidate.label, order_index: prev.length, is_default: prev.length === 0 }]));
    setAudienceAssignments((prev) => {
      if (prev.some((a) => a.resource_id === candidate.resource_id && a.audience_key === activeTab)) return prev;
      const tabCount = prev.filter((a) => a.audience_key === activeTab).length;
      return [...prev, { resource_id: candidate.resource_id, audience_key: activeTab, order_index: tabCount, is_default: tabCount === 0 }];
    });
    if (resourceType === "work_document" && candidate.blocks?.length && baseCandidates?.participants?.length && !alreadyInPlan) {
      setOverrides((prev) => [
        ...prev,
        ...candidate.blocks.flatMap((b) =>
          baseCandidates.participants.map((p) => ({ resource_id: candidate.resource_id, block_id: b.block_id, participant_member_id: p.membership_id, visible: true, editable: true }))
        ),
      ]);
    }
  }
  function removeAssignment(resourceId) {
    setAudienceAssignments((prev) => {
      const remaining = prev.filter((a) => !(a.resource_id === resourceId && a.audience_key === activeTab));
      const tabRemaining = remaining.filter((a) => a.audience_key === activeTab).sort((x, y) => x.order_index - y.order_index);
      const others = remaining.filter((a) => a.audience_key !== activeTab);
      const hasDefault = tabRemaining.some((a) => a.is_default);
      const renumbered = tabRemaining.map((a, i) => ({ ...a, order_index: i, is_default: !hasDefault && i === 0 ? true : a.is_default }));
      const next = [...others, ...renumbered];
      const stillAssignedElsewhere = next.some((a) => a.resource_id === resourceId);
      if (!stillAssignedElsewhere) {
        setResources((r) => r.filter((res) => res.resource_id !== resourceId));
        setOverrides((o) => o.filter((ov) => ov.resource_id !== resourceId));
      }
      return next;
    });
  }
  function moveAssignment(resourceId, dir) {
    setAudienceAssignments((prev) => {
      const tab = prev.filter((a) => a.audience_key === activeTab).sort((x, y) => x.order_index - y.order_index);
      const others = prev.filter((a) => a.audience_key !== activeTab);
      const idx = tab.findIndex((a) => a.resource_id === resourceId);
      const swap = idx + dir;
      if (idx === -1 || swap < 0 || swap >= tab.length) return prev;
      [tab[idx], tab[swap]] = [tab[swap], tab[idx]];
      return [...others, ...tab.map((a, i) => ({ ...a, order_index: i }))];
    });
  }
  function setTabDefault(resourceId) {
    setAudienceAssignments((prev) => prev.map((a) => (a.audience_key === activeTab ? { ...a, is_default: a.resource_id === resourceId } : a)));
  }

  function addTool(candidate) {
    setResources((prev) => (prev.some((r) => r.resource_id === candidate.resource_id) ? prev : [...prev, { resource_id: candidate.resource_id, resource_type: "tool", label: candidate.label, order_index: prev.length, is_default: false }]));
  }
  function removeTool(resourceId) {
    setResources((prev) => prev.filter((r) => r.resource_id !== resourceId));
  }

  function toggleOverride(resourceId, blockId, participantMemberId, field) {
    setOverrides((prev) => {
      const existing = prev.find((o) => o.resource_id === resourceId && o.block_id === blockId && o.participant_member_id === participantMemberId);
      if (existing) return prev.map((o) => (o === existing ? { ...o, [field]: !o[field] } : o));
      const base = { resource_id: resourceId, block_id: blockId, participant_member_id: participantMemberId, visible: true, editable: true };
      return [...prev, { ...base, [field]: !base[field] }];
    });
  }
  function toggleAllEditable(resourceId, blockIds, participantIds) {
    setOverrides((prev) => {
      const allCurrentlyEditable = blockIds.every((blockId) =>
        participantIds.every((pid) => {
          const o = prev.find((ov) => ov.resource_id === resourceId && ov.block_id === blockId && ov.participant_member_id === pid);
          return o ? o.editable : true;
        })
      );
      const nextEditable = !allCurrentlyEditable;
      let next = prev;
      for (const blockId of blockIds) {
        for (const pid of participantIds) {
          const existing = next.find((o) => o.resource_id === resourceId && o.block_id === blockId && o.participant_member_id === pid);
          if (existing) next = next.map((o) => (o === existing ? { ...o, editable: nextEditable } : o));
          else next = [...next, { resource_id: resourceId, block_id: blockId, participant_member_id: pid, visible: true, editable: nextEditable }];
        }
      }
      return next;
    });
  }

  async function handleSave() {
    try {
      await savePlan.mutateAsync({
        resources: resources.map(({ resource_id, resource_type, order_index, is_default }) => ({ resource_id, resource_type, order_index, is_default })),
        block_overrides: overrides,
        audience_assignments: audienceAssignments,
      });
      toast.success("Session prep saved");
    } catch (err) {
      toast.error(err.body?.detail || "Couldn't save session prep");
    }
  }

  if (candidatesPending || planPending) return <p className="text-sm text-ink-soft" data-testid="session-prep-loading">Loading session prep…</p>;
  if (candidatesError || planError || !baseCandidates) return <p className="text-sm text-red-600" data-testid="session-prep-error">Couldn't load session prep.</p>;

  const workDocuments = baseCandidates.work_documents.filter((d) => resources.some((r) => r.resource_id === d.resource_id));
  const activeTabLabel = AUDIENCE_LABELS[activeTab] || audienceTabs.find((t) => t.key === activeTab)?.label || activeTab;
  const activeAssignments = audienceAssignments.filter((a) => a.audience_key === activeTab).sort((x, y) => x.order_index - y.order_index);
  const resourceById = Object.fromEntries(resources.map((r) => [r.resource_id, r]));

  return (
    <div data-testid="session-prep-view">
      <h2 className="font-serif text-lg text-ink mb-2">Session Resources</h2>
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="session-prep-audience-tabs">
        {audienceTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setCandidateSearch(""); }}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${activeTab === t.key ? "bg-ink text-paper border-ink" : "border-ink/15 text-ink/60 hover:border-ink/30"}`}
            data-testid={`session-prep-audience-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs text-ink-soft uppercase">Available for {activeTabLabel}</p>
            <input
              type="text"
              value={candidateSearch}
              onChange={(e) => setCandidateSearch(e.target.value)}
              placeholder="Search resources…"
              className="text-xs border border-ink/15 rounded-full px-3 py-1 w-40 bg-white/70 focus:outline-none focus:border-terracotta"
              data-testid="session-prep-candidate-search"
            />
          </div>
          {DOCUMENT_GROUPS.map(([type, key, label]) => {
            const items = (tabCandidates?.[key] || []).filter((c) => c.label.toLowerCase().includes(candidateSearch.trim().toLowerCase()));
            if (!(tabCandidates?.[key] || []).length) return null;
            return (
            <div key={type} className="mb-4">
              <p className="text-xs text-ink-soft uppercase mb-1.5">{label}</p>
              {items.length === 0 ? (
                <p className="text-xs text-ink-soft/70">{candidateSearch ? "No matches." : "None available."}</p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {items.map((c) => {
                    const assigned = activeAssignments.some((a) => a.resource_id === c.resource_id);
                    return (
                      <div key={c.resource_id} className="flex items-center justify-between gap-2 border border-ink/10 rounded-md px-2.5 py-1.5 bg-white/50" data-testid={`prep-candidate-${activeTab}-${c.resource_id}`}>
                        <span className="text-sm text-ink truncate">{c.label}</span>
                        <button
                          onClick={() => addResourceToActiveTab(type, c)}
                          disabled={assigned}
                          className="shrink-0 text-xs flex items-center gap-1 text-terracotta disabled:text-ink-soft disabled:opacity-50"
                          data-testid={`prep-add-${activeTab}-${c.resource_id}`}
                        >
                          <Plus size={12} /> {assigned ? "Added" : "Add"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-ink-soft uppercase">Selected for {activeTabLabel}</p>
            <button
              onClick={handleSave}
              disabled={savePlan.isPending}
              className="text-sm font-medium bg-terracotta text-paper px-4 py-1.5 rounded-full hover:bg-terracotta/90 transition-colors disabled:opacity-50"
              data-testid="session-prep-save-button"
            >
              {savePlan.isPending ? "Saving…" : "Save"}
            </button>
          </div>
          {activeAssignments.length === 0 ? (
            <p className="text-sm text-ink-soft" data-testid="session-prep-selected-empty">No resources selected yet for {activeTabLabel}.</p>
          ) : (
            <div className="space-y-2" data-testid="session-prep-selected-list">
              {activeAssignments.map((a, i) => {
                const r = resourceById[a.resource_id];
                if (!r) return null;
                return (
                  <div key={a.resource_id} className="border border-ink/10 rounded-lg p-2.5 bg-white/50" data-testid={`session-prep-selected-${activeTab}-${a.resource_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-ink truncate">{r.label}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => setTabDefault(a.resource_id)} className={`text-xs px-2 py-0.5 rounded-full ${a.is_default ? "bg-ink text-paper" : "text-ink-soft border border-ink/15"}`} data-testid={`session-prep-set-default-${activeTab}-${a.resource_id}`}>Default</button>
                        <button onClick={() => moveAssignment(a.resource_id, -1)} disabled={i === 0} className="text-ink-soft disabled:opacity-30" data-testid={`session-prep-move-up-${activeTab}-${a.resource_id}`}><ArrowUp size={13} /></button>
                        <button onClick={() => moveAssignment(a.resource_id, 1)} disabled={i === activeAssignments.length - 1} className="text-ink-soft disabled:opacity-30" data-testid={`session-prep-move-down-${activeTab}-${a.resource_id}`}><ArrowDown size={13} /></button>
                        <button onClick={() => removeAssignment(a.resource_id)} className="text-ink-soft hover:text-red-600" data-testid={`session-prep-remove-${activeTab}-${a.resource_id}`}><X size={13} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 border-t border-ink/10 pt-4">
        <p className="text-xs text-ink-soft uppercase mb-2">Session Tools</p>
        <p className="text-xs text-ink-soft/70 mb-2">Available to everyone in the live Room — never tied to a specific tab.</p>
        <div className="flex flex-wrap gap-1.5" data-testid="session-prep-tools-list">
          {(baseCandidates.tools || []).map((c) => {
            const added = resources.some((r) => r.resource_id === c.resource_id);
            return (
              <button
                key={c.resource_id}
                onClick={() => (added ? removeTool(c.resource_id) : addTool(c))}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${added ? "bg-ink text-paper border-ink" : "border-ink/15 text-ink/60 hover:border-ink/30"}`}
                data-testid={`session-prep-tool-${c.resource_id}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {workDocuments.length > 0 && (
        <div className="mt-6 border-t border-ink/10 pt-4">
          <p className="text-xs text-ink-soft uppercase mb-2">Document Block Access</p>
          <div className="space-y-2">
            {workDocuments.map((d) => (
              <div key={d.resource_id} className="border border-ink/10 rounded-lg p-2.5 bg-white/50">
                <p className="text-sm text-ink">{d.label}</p>
                <BlockOverrideEditor
                  resource={d}
                  participants={baseCandidates.participants}
                  overrides={overrides.filter((o) => o.resource_id === d.resource_id)}
                  onToggle={toggleOverride}
                  onToggleAll={toggleAllEditable}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 border-t border-ink/10 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs text-ink-soft uppercase">{planned ? "Preview" : "Preview as"}</p>
          {!planned && (
            <Select value={previewFor || ""} onValueChange={setPreviewFor}>
              <SelectTrigger className="h-7 text-xs w-40" data-testid="session-prep-preview-participant-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {baseCandidates.participants.map((p) => <SelectItem key={p.membership_id} value={p.membership_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        {planned && (
          <p className="text-xs text-ink-soft mb-2" data-testid="session-prep-planned-note">
            No Session is booked yet — this plan carries over automatically the moment one is.
          </p>
        )}
        {preview && (
          <div className="border border-ink/10 rounded-lg p-3 bg-ink/5" data-testid="session-prep-preview-panel">
            {!planned && (
              <p className="text-xs text-ink-soft mb-2">Exact Room view for {baseCandidates.participants.find((p) => p.membership_id === previewFor)?.name} · {preview.viewer_capabilities?.can_edit_blocks ? "can edit" : "read-only"}</p>
            )}
            {!planned && preview.audience_tabs?.length > 0 ? (
              <div className="space-y-3">
                {preview.audience_tabs.map((t) => (
                  <div key={t.audience_key}>
                    <p className="text-xs font-semibold text-ink uppercase mb-1" data-testid={`session-prep-preview-tab-${t.audience_key}`}>
                      {AUDIENCE_LABELS[t.audience_key] || audienceTabs.find((at) => at.key === t.audience_key)?.label || t.audience_key}
                    </p>
                    <ol className="space-y-1 pl-2">
                      {t.resources.map((r) => (
                        <li key={r.resource_id} className="text-sm text-ink" data-testid={`session-prep-preview-resource-${t.audience_key}-${r.resource_id}`}>
                          {r.resource_id === t.default_resource_id ? "★ " : ""}{r.label}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            ) : (
              <ol className="space-y-1">
                {preview.resources.map((r) => (
                  <li key={r.resource_id} className="text-sm text-ink" data-testid={`session-prep-preview-resource-${r.resource_id}`}>
                    {r.resource_id === preview.default_resource_id ? "★ " : ""}{r.label}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BlockOverrideEditor({ resource, participants, overrides, onToggle, onToggleAll }) {
  if (!resource?.blocks?.length) return null;
  const blockIds = resource.blocks.map((b) => b.block_id);
  const participantIds = participants.map((p) => p.membership_id);
  const allEditable = blockIds.every((blockId) =>
    participantIds.every((pid) => {
      const o = overrides.find((ov) => ov.block_id === blockId && ov.participant_member_id === pid);
      return o ? o.editable : true;
    })
  );
  return (
    <div className="mt-2 pl-2 border-l border-ink/10 space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-soft uppercase">Blocks</p>
        <button
          onClick={() => onToggleAll(resource.resource_id, blockIds, participantIds)}
          className="text-xs font-medium text-terracotta hover:text-terracotta/80"
          data-testid={`session-prep-toggle-all-editable-${resource.resource_id}`}
        >
          {allEditable ? "Make all read-only" : "Make all editable"}
        </button>
      </div>
      {resource.blocks.map((b) => (
        <div key={b.block_id} className="space-y-1">
          <p className="text-xs text-ink-soft">{b.block_key}</p>
          {participants.map((p) => {
            const o = overrides.find((ov) => ov.block_id === b.block_id && ov.participant_member_id === p.membership_id);
            return (
              <div key={p.membership_id} className="flex items-center justify-between text-xs text-ink pl-2">
                <span>{p.name}</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1"><Switch checked={o ? o.visible : true} onCheckedChange={() => onToggle(resource.resource_id, b.block_id, p.membership_id, "visible")} data-testid={`session-prep-override-visible-${b.block_id}-${p.membership_id}`} /> Visible</label>
                  <label className="flex items-center gap-1"><Switch checked={o ? o.editable : true} onCheckedChange={() => onToggle(resource.resource_id, b.block_id, p.membership_id, "editable")} data-testid={`session-prep-override-editable-${b.block_id}-${p.membership_id}`} /> Editable</label>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
